import React, { useState, useMemo } from 'react';
import {
  ChevronDown, Flag, Layers, AlertTriangle, Calendar, User, Video,
  Repeat, Clock, Sparkles, Database, Plus,
} from 'lucide-react';
import { TASK_FIELD_GROUPS, TASK_FIELD_OPTIONS } from '../../../design-system/taskFieldGroups';

/**
 * OrkaTaskFieldGroups
 * -------------------
 * V4 reconciliation primitive — renders a task as independently-collapsible
 * field groups (Identity / Classification / Status & Priority / Schedule /
 * People / Meet / Recurrence / Effort / Planning / System).
 *
 * Consumers:
 *   - TaskCard.jsx (open body)
 *   - TaskModal.jsx (edit form — future)
 *   - DesignSystemPreviewPage (design-system embed — already has its own
 *     self-contained preview for design iteration)
 *
 * Props:
 *   task      the task document (any V3/V4 shape; fields unknown to a group
 *             are rendered as "—")
 *   onChange  (key, value) => void — called on every smart-control edit
 *   groups    override/subset of TASK_FIELD_GROUPS (default = all 10)
 *   options   runtime overrides for select options (spread over TASK_FIELD_OPTIONS)
 */

const ICONS = { flag: Flag, layers: Layers, alert: AlertTriangle, calendar: Calendar, user: User, video: Video, repeat: Repeat, clock: Clock, spark: Sparkles, database: Database };

function SmartControl({ value, kind, optionKey, options, onChange }) {
  const commit = (v) => onChange?.(v);
  if (kind === 'select') {
    const list = options[optionKey] || [];
    const items = list.map((i) => (typeof i === 'string' ? { value: i, label: i } : i));
    return (
      <select className="w-full min-h-9 px-2.5 rounded-lg border orka-border-subtle orka-surface-raised orka-text-primary text-[13px] font-medium" value={value ?? ''} onChange={(e) => commit(e.target.value)}>
        <option value="">—</option>
        {items.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (kind === 'person') {
    const list = options.people || [];
    return (
      <select className="w-full min-h-9 px-2.5 rounded-lg border orka-border-subtle orka-surface-raised orka-text-primary text-[13px] font-medium" value={value ?? ''} onChange={(e) => commit(e.target.value)}>
        <option value="">—</option>
        {list.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
    );
  }
  if (kind === 'people') {
    const arr = String(value || '').split(',').map((s) => s.trim()).filter(Boolean);
    return (
      <div className="flex flex-wrap gap-1.5 mt-1">
        {arr.map((p) => (
          <span key={p} className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border orka-border-subtle orka-surface-raised text-[12px] font-semibold orka-text-primary">
            <User className="w-3 h-3" /> {p}
          </span>
        ))}
        <button type="button" className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border orka-border-subtle orka-surface-raised text-[11px] font-semibold orka-text-secondary hover:orka-surface-subtle">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
    );
  }
  if (kind === 'textarea') {
    return <textarea className="w-full min-h-[72px] p-2.5 rounded-lg border orka-border-subtle orka-surface-raised orka-text-primary text-[13px] font-medium leading-snug resize-y" value={value || ''} onChange={(e) => commit(e.target.value)} />;
  }
  if (kind === 'date') {
    return <input type="date" className="w-full min-h-9 px-2.5 rounded-lg border orka-border-subtle orka-surface-raised orka-text-primary text-[13px] font-medium" value={value || ''} onChange={(e) => commit(e.target.value)} />;
  }
  if (kind === 'time') {
    return <input type="time" className="w-full min-h-9 px-2.5 rounded-lg border orka-border-subtle orka-surface-raised orka-text-primary text-[13px] font-medium" value={value || ''} onChange={(e) => commit(e.target.value)} />;
  }
  if (kind === 'number') {
    return <input type="number" className="w-full min-h-9 px-2.5 rounded-lg border orka-border-subtle orka-surface-raised orka-text-primary text-[13px] font-medium" value={value ?? 0} onChange={(e) => commit(e.target.value)} />;
  }
  if (kind === 'toggle') {
    const on = !!value;
    return (
      <button type="button" onClick={() => commit(!on)} className="w-full flex items-center justify-between min-h-9 px-3 rounded-lg border orka-border-subtle orka-surface-raised text-[13px] font-semibold orka-text-primary">
        <span>{on ? 'Enabled' : 'Disabled'}</span>
        <span className={`w-9 h-5 rounded-full relative orka-transition-colors ${on ? 'bg-emerald-500' : 'orka-surface-subtle'}`}>
          <span className={`absolute top-[3px] w-[14px] h-[14px] rounded-full bg-white orka-transition-colors ${on ? 'right-[3px]' : 'left-[3px]'}`} />
        </span>
      </button>
    );
  }
  if (kind === 'link') {
    return value ? (
      <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" className="flex items-center justify-between min-h-9 px-3 rounded-lg border orka-border-strong orka-bg-info-soft text-[13px] font-semibold orka-text-info hover:underline">
        <span className="truncate">{value}</span>
        <span>↗</span>
      </a>
    ) : <div className="min-h-9 px-3 flex items-center rounded-lg border orka-border-subtle orka-surface-raised orka-text-tertiary text-[12px] font-mono">—</div>;
  }
  if (kind === 'readonly') {
    return <div className="min-h-9 px-3 flex items-center rounded-lg border orka-border-subtle orka-surface-raised orka-text-tertiary text-[12px] font-mono">{value != null && value !== '' ? String(value) : '—'}</div>;
  }
  return <input type="text" className="w-full min-h-9 px-2.5 rounded-lg border orka-border-subtle orka-surface-raised orka-text-primary text-[13px] font-medium" value={value || ''} onChange={(e) => commit(e.target.value)} />;
}

function Field({ label, value, kind, optionKey, options, onChange }) {
  const wide = kind === 'textarea' || optionKey === 'wide';
  return (
    <div className={`p-3 rounded-xl border orka-border-subtle orka-surface-base ${wide ? 'col-span-full' : ''}`}>
      <div className="text-[10px] uppercase tracking-wider orka-text-tertiary font-bold">{label}</div>
      <div className="mt-1.5">
        <SmartControl value={value} kind={kind} optionKey={optionKey} options={options} onChange={onChange} />
      </div>
    </div>
  );
}

function Group({ group, task, options, onChange }) {
  const [open, setOpen] = useState(group.defaultOpen);
  const Icon = ICONS[group.iconKey] || Flag;
  return (
    <section className={`rounded-2xl border orka-border-subtle ${group.meta ? 'opacity-80 orka-surface-subtle' : 'orka-surface-raised'} overflow-hidden`} data-testid={`task-field-group-${group.id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={open}
        data-testid={`task-field-group-${group.id}-toggle`}
      >
        <span className="flex items-center gap-2.5 font-bold orka-text-primary text-[14px]">
          <span className="w-7 h-7 rounded-lg grid place-items-center border orka-border-subtle orka-surface-base orka-text-tertiary">
            <Icon className="w-3.5 h-3.5" />
          </span>
          {group.title}
        </span>
        <span className="flex items-center gap-2">
          <span className="text-[11px] orka-text-tertiary">{group.fields.length} fields</span>
          <ChevronDown className={`w-4 h-4 orka-text-tertiary orka-transition-colors ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="p-3 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            {group.fields.map(([label, key, kind, optionKey]) => (
              <Field
                key={key}
                label={label}
                value={task?.[key]}
                kind={kind}
                optionKey={optionKey}
                options={options}
                onChange={(v) => onChange?.(key, v)}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export const OrkaTaskFieldGroups = ({
  task = {},
  onChange,
  groups = TASK_FIELD_GROUPS,
  options: optOverrides = {},
}) => {
  const options = useMemo(() => ({ ...TASK_FIELD_OPTIONS, ...optOverrides }), [optOverrides]);
  return (
    <div className="space-y-3" data-testid="orka-task-field-groups">
      {groups.map((g) => <Group key={g.id} group={g} task={task} options={options} onChange={onChange} />)}
    </div>
  );
};
